import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

const countyOptions = [
  "Albany", "Allegany", "Bronx", "Broome", "Cattaraugus", "Cayuga",
  "Chautauqua", "Chemung", "Chenango", "Clinton", "Columbia", "Cortland",
  "Delaware", "Dutchess", "Erie", "Essex", "Franklin", "Fulton", "Genesee",
  "Greene", "Hamilton", "Herkimer", "Jefferson", "Kings", "Lewis",
  "Livingston", "Madison", "Monroe", "Montgomery", "Nassau", "New York",
  "Niagara", "Oneida", "Onondaga", "Ontario", "Orange", "Orleans", "Oswego",
  "Otsego", "Putnam", "Queens", "Rensselaer", "Richmond", "Rockland",
  "Saratoga", "Schenectady", "Schoharie", "Schuyler", "Seneca",
  "St. Lawrence", "Steuben", "Suffolk", "Sullivan", "Tioga", "Tompkins",
  "Ulster", "Warren", "Washington", "Wayne", "Westchester", "Wyoming",
  "Yates", "Other",
];

export default function FamilySupportRequest() {
  const [formData, setFormData] = useState({
    family_name: "",
    email: "",
    phone_number: "",
    county: "",
    service_looking_for: "",
    hourly_rate: "",
    days_looking_for: "",
    notes: "",
  });

  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  async function fetchRequests() {
    setLoadingRequests(true);

    const { data, error } = await supabase
      .from("family_support_requests")
      .select("*")
      .eq("approved", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Supabase fetch error:", error);
      setRequests([]);
    } else {
      setRequests(data || []);
    }

    setLoadingRequests(false);
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function formatPhone(phone) {
    if (!phone) return "Not provided";

    const cleaned = String(phone).replace(/\D/g, "");

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    return phone;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    const { error } = await supabase.from("family_support_requests").insert([
      {
        family_name: formData.family_name,
        email: formData.email,
        phone_number: formData.phone_number || null,
        county: formData.county,
        service_looking_for: formData.service_looking_for,
        hourly_rate: formData.hourly_rate,
        days_looking_for: formData.days_looking_for,
        notes: formData.notes,
        approved: false,
      },
    ]);

    if (error) {
      console.error("Supabase insert error:", error);
      setErrorMessage(error.message || "There was an error submitting your request.");
    } else {
      setSuccessMessage(
        "Thank you! Your request has been submitted and is pending approval."
      );

      setFormData({
        family_name: "",
        email: "",
        phone_number: "",
        county: "",
        service_looking_for: "",
        hourly_rate: "",
        days_looking_for: "",
        notes: "",
      });

      await fetchRequests();
    }

    setSubmitting(false);
  }

  return (
    <div className="page">
      <section className="card">
        <h1>Families Seeking Support</h1>

        <p className="intro">
          Looking for Community Habilitation, Respite, or another support
          service? Complete the brief form below to be added to our family
          support request list.
        </p>

        <form onSubmit={handleSubmit} className="family-form">
          <div className="form-grid">
            <div className="form-field">
              <label>Family / Contact Name</label>
              <input
                type="text"
                name="family_name"
                value={formData.family_name}
                onChange={handleChange}
                placeholder="Enter your name"
                required
              />
            </div>

            <div className="form-field">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-field">
              <label>
                Phone Number <span className="optional">(optional)</span>
              </label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-field">
              <label>County</label>
              <select
                name="county"
                value={formData.county}
                onChange={handleChange}
                required
              >
                <option value="">Select County</option>
                {countyOptions.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Service Looking For</label>
              <select
                name="service_looking_for"
                value={formData.service_looking_for}
                onChange={handleChange}
                required
              >
                <option value="">Select Service</option>
                <option value="ComHab">ComHab</option>
                <option value="Respite">Respite</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-field">
              <label>Hourly Rate</label>
              <input
                type="text"
                name="hourly_rate"
                value={formData.hourly_rate}
                onChange={handleChange}
                placeholder="Example: $25/hour"
              />
            </div>

            <div className="form-field full-width">
              <label>Days Looking For</label>
              <input
                type="text"
                name="days_looking_for"
                value={formData.days_looking_for}
                onChange={handleChange}
                placeholder="Example: Monday, Wednesday, weekends"
                required
              />
            </div>

            <div className="form-field full-width">
              <label>Additional Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Share schedule needs, location details, preferred experience, or anything helpful."
                rows="5"
              />
            </div>
          </div>

          {successMessage && <p className="success-message">{successMessage}</p>}
          {errorMessage && <p className="error-message">{errorMessage}</p>}

          <div className="form-actions">
            <button type="submit" className="contact-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Sign Up to Join List"}
            </button>
          </div>
        </form>

        <hr className="section-divider" />

        <h2 className="table-title">Current Family Support Requests</h2>

        <p className="table-note">
          Public list displays approved requests only. Click View Details to see
          the full request information.
        </p>

        {loadingRequests ? (
          <p className="status-message">Loading requests...</p>
        ) : (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Contact Name</th>
                  <th>County</th>
                  <th>Service</th>
                  <th>Hourly Rate</th>
                  <th>Details</th>
                </tr>
              </thead>

              <tbody>
                {requests.length > 0 ? (
                  requests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.family_name || "Not provided"}</td>
                      <td>{request.county || "Not provided"}</td>
                      <td>{request.service_looking_for || "Not provided"}</td>
                      <td>{request.hourly_rate || "Not provided"}</td>
                      <td>
                        <button
                          type="button"
                          className="contact-btn"
                          onClick={() => setSelectedRequest(request)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-results">
                      No approved family support requests yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {selectedRequest && (
          <div
            className="details-modal-overlay"
            onClick={() => setSelectedRequest(null)}
          >
            <div
              className="details-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h2>{selectedRequest.family_name || "Family Request"}</h2>

              <p className="details-modal-subtitle">
                Family Support Request
              </p>

              <div className="contact-highlight">
                <div className="detail-box">
                  <strong>Email:</strong>
                  <br />
                  {selectedRequest.email ? (
                    <a href={`mailto:${selectedRequest.email}`}>
                      {selectedRequest.email}
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </div>

                <div className="detail-box">
                  <strong>Phone:</strong>
                  <br />
                  {formatPhone(selectedRequest.phone_number)}
                </div>
              </div>

              <div className="detail-grid">
                <div className="detail-box">
                  <strong>County:</strong>{" "}
                  {selectedRequest.county || "Not provided"}
                </div>

                <div className="detail-box">
                  <strong>Service Looking For:</strong>{" "}
                  {selectedRequest.service_looking_for || "Not provided"}
                </div>

                <div className="detail-box">
                  <strong>Hourly Rate:</strong>{" "}
                  {selectedRequest.hourly_rate || "Not provided"}
                </div>

                <div className="detail-box">
                  <strong>Days Looking For:</strong>{" "}
                  {selectedRequest.days_looking_for || "Not provided"}
                </div>

                <div className="detail-box full-width">
                  <strong>Notes:</strong>
                  <br />
                  {selectedRequest.notes || "Not provided"}
                </div>
              </div>

              <div className="modal-actions">
                {selectedRequest.email && (
                  <a
                    href={`mailto:${selectedRequest.email}`}
                    className="contact-btn"
                  >
                    Email Family
                  </a>
                )}

                <button
                  type="button"
                  className="contact-btn"
                  onClick={() => setSelectedRequest(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .page {
            width: 100%;
            padding: 20px;
          }

          .card {
            background: white;
            width: 100%;
            max-width: 1100px;
            margin: 0 auto;
            padding: 32px;
            border-radius: 20px;
            box-shadow: 0 10px 30px rgba(0,0,0,.08);
          }

          h1 {
            text-align: center;
            margin-bottom: 10px;
            color: #111827;
          }

          .intro {
            text-align: center;
            max-width: 800px;
            margin: 0 auto 28px auto;
            color: #4b5563;
            line-height: 1.6;
          }

          .family-form {
            width: 100%;
          }

          .form-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 18px;
          }

          .form-field {
            display: flex;
            flex-direction: column;
            text-align: left;
          }

          .form-field label {
            font-weight: 600;
            margin-bottom: 7px;
            color: #374151;
          }

          .optional {
            font-weight: 400;
            color: #6b7280;
            font-size: 0.9rem;
          }

          .form-field input,
          .form-field select,
          .form-field textarea {
            width: 100%;
            padding: 14px;
            border-radius: 10px;
            border: 1px solid #ddd;
            font-size: 15px;
            font-family: inherit;
          }

          .form-field textarea {
            resize: vertical;
          }

          .full-width {
            grid-column: 1 / -1;
          }

          .form-actions {
            display: flex;
            justify-content: center;
            margin-top: 24px;
          }

          .contact-btn {
            background: #2f6f89;
            color: white;
            border: none;
            padding: 10px 16px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            white-space: nowrap;
            text-decoration: none;
            display: inline-block;
            font-size: 15px;
          }

          .contact-btn:hover {
            background: #25596e;
          }

          .contact-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .success-message {
            text-align: center;
            margin-top: 18px;
            padding: 14px;
            border-radius: 10px;
            background: #ecfdf5;
            color: #047857;
            font-weight: 600;
          }

          .error-message {
            text-align: center;
            margin-top: 18px;
            padding: 14px;
            border-radius: 10px;
            background: #fef2f2;
            color: #a33a3a;
            font-weight: 600;
          }

          .section-divider {
            margin: 42px 0 28px;
            border: none;
            border-top: 1px solid #e5e7eb;
          }

          .table-title {
            text-align: center;
            margin-bottom: 8px;
            color: #111827;
          }

          .table-note {
            text-align: center;
            max-width: 760px;
            margin: 0 auto 22px auto;
            color: #6b7280;
            line-height: 1.6;
            font-size: 0.95rem;
          }

          .status-message,
          .no-results {
            text-align: center;
            padding: 20px;
          }

          .tableWrap {
            width: 100%;
            overflow-x: auto;
            border: 1px solid #e5e7eb;
            border-radius: 14px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            min-width: 760px;
            background: #fff;
          }

          th {
            background: #2f6f89;
            color: white;
            padding: 14px 12px;
            text-align: left;
            font-size: 15px;
          }

          td {
            padding: 14px 12px;
            border-bottom: 1px solid #eee;
            vertical-align: top;
            color: #1f2937;
          }

          tbody tr:hover {
            background: #f7fafc;
          }

          .details-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.35);
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 20px;
            z-index: 1000;
          }

          .details-modal {
            background: white;
            width: 100%;
            max-width: 760px;
            max-height: 85vh;
            overflow-y: auto;
            padding: 28px;
            border-radius: 18px;
            box-shadow: 0 10px 40px rgba(0,0,0,.2);
          }

          .details-modal h2 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #1f2937;
            text-align: center;
          }

          .details-modal-subtitle {
            margin-bottom: 22px;
            color: #6b7280;
            text-align: center;
          }

          .contact-highlight {
            background: #eef6fa;
            border: 1px solid #d6e6ef;
            border-radius: 14px;
            padding: 18px;
            margin-bottom: 20px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            font-size: 1.05rem;
          }

          .detail-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px 20px;
          }

          .detail-box {
            line-height: 1.5;
            word-break: break-word;
            background: #f8fafc;
            border: 1px solid #e6edf2;
            border-radius: 12px;
            padding: 12px 14px;
          }

          .detail-box a {
            color: #2f6f89;
          }

          .modal-actions {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 24px;
          }

          @media (max-width: 700px) {
            .page {
              padding: 12px;
            }

            .card {
              padding: 20px;
            }

            .form-grid,
            .detail-grid,
            .contact-highlight {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </section>
    </div>
  );
}